import { createTheme } from "@mui/material/styles";

/**
 * MUI theme built on the design tokens in `src/style/tokens.css`.
 *
 * The palette is resolved in JS (MUI needs real colour values for its own
 * contrast maths), while everything structural — surfaces, borders, radii,
 * shadows — reads the CSS custom properties so a theme switch is a single
 * attribute flip on <html> with no React re-paint of colours.
 */

const FONT_STACK =
  '"Poppins", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const PALETTE = {
  light: {
    primary: { main: "#2a78d6", dark: "#1c5cab", light: "#5598e7" },
    error: { main: "#d03b3b" },
    warning: { main: "#fab219" },
    success: { main: "#0ca30c" },
    info: { main: "#2a78d6" },
    text: { primary: "#0f172a", secondary: "#475569", disabled: "#94a3b8" },
    divider: "#e4e8ee",
    background: { default: "#f4f6f9", paper: "#ffffff" },
  },
  dark: {
    primary: { main: "#3987e5", dark: "#256abf", light: "#6da7ec" },
    error: { main: "#e66767" },
    warning: { main: "#f5bf4c" },
    success: { main: "#3cc93c" },
    info: { main: "#3987e5" },
    text: { primary: "#eef2f7", secondary: "#b3c0d1", disabled: "#64748b" },
    divider: "#222e40",
    background: { default: "#0a0e14", paper: "#121822" },
  },
};

export const buildTheme = (mode = "light") =>
  createTheme({
    palette: { mode, ...PALETTE[mode] },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: FONT_STACK,
      // A single scale, matching the CSS tokens. No weight above 600 anywhere:
      // heavy weights were the main source of visual noise in the old UI.
      h1: { fontSize: "2.25rem", fontWeight: 600, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-0.015em" },
      h3: { fontSize: "1.375rem", fontWeight: 600, letterSpacing: "-0.01em" },
      h4: { fontSize: "1.125rem", fontWeight: 600 },
      h5: { fontSize: "1rem", fontWeight: 600 },
      h6: { fontSize: "0.9375rem", fontWeight: 600 },
      subtitle1: { fontSize: "0.9375rem", fontWeight: 500 },
      subtitle2: { fontSize: "0.8125rem", fontWeight: 600 },
      body1: { fontSize: "0.875rem", lineHeight: 1.6 },
      body2: { fontSize: "0.8125rem", lineHeight: 1.6 },
      caption: { fontSize: "0.75rem", lineHeight: 1.5 },
      button: { fontSize: "0.8125rem", fontWeight: 600, textTransform: "none" },
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },

    components: {
      /* ---------------------------------------------------------------- */
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: "var(--wg-page)",
            color: "var(--wg-ink)",
          },
        },
      },

      /* --- Buttons ------------------------------------------------------ */
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-md)",
            textTransform: "none",
            fontWeight: 600,
            paddingInline: 14,
            minHeight: 36,
            transition:
              "background-color var(--wg-duration) var(--wg-ease), border-color var(--wg-duration) var(--wg-ease), color var(--wg-duration) var(--wg-ease)",
            "&.Mui-focusVisible": { boxShadow: "var(--wg-shadow-focus)" },
          },
          sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: "0.8125rem" },
          sizeLarge: { minHeight: 44, paddingInline: 20, fontSize: "0.9375rem" },
          contained: {
            backgroundColor: "var(--wg-primary)",
            color: "var(--wg-primary-ink)",
            "&:hover": { backgroundColor: "var(--wg-primary-hover)" },
            "&.Mui-disabled": {
              backgroundColor: "var(--wg-surface-hover)",
              color: "var(--wg-ink-muted)",
            },
          },
          containedError: { backgroundColor: "var(--wg-critical)" },
          outlined: {
            borderColor: "var(--wg-border-strong)",
            color: "var(--wg-ink)",
            backgroundColor: "var(--wg-surface)",
            "&:hover": {
              borderColor: "var(--wg-primary-border)",
              backgroundColor: "var(--wg-primary-soft)",
              color: "var(--wg-primary)",
            },
            "&.Mui-disabled": {
              borderColor: "var(--wg-border)",
              color: "var(--wg-ink-muted)",
              backgroundColor: "transparent",
            },
          },
          text: {
            color: "var(--wg-ink-secondary)",
            "&:hover": {
              backgroundColor: "var(--wg-surface-hover)",
              color: "var(--wg-ink)",
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-sm)",
            color: "var(--wg-ink-secondary)",
            "&:hover": {
              backgroundColor: "var(--wg-surface-hover)",
              color: "var(--wg-ink)",
            },
            "&.Mui-focusVisible": { boxShadow: "var(--wg-shadow-focus)" },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-sm)",
            textTransform: "none",
            fontWeight: 600,
            borderColor: "var(--wg-border)",
            color: "var(--wg-ink-secondary)",
            "&.Mui-selected": {
              backgroundColor: "var(--wg-primary-soft)",
              color: "var(--wg-primary)",
            },
          },
        },
      },

      /* --- Surfaces ----------------------------------------------------- */
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: "var(--wg-surface)",
            color: "var(--wg-ink)",
          },
          outlined: { borderColor: "var(--wg-border)" },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: "1px solid var(--wg-border)",
            borderRadius: "var(--wg-radius-lg)",
            boxShadow: "var(--wg-shadow-sm)",
            overflow: "hidden",
          },
        },
      },

      /* --- Dialogs ------------------------------------------------------ */
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--wg-overlay)",
            backdropFilter: "blur(2px)",
          },
        },
      },

      MuiDialog: {
        defaultProps: { scroll: "paper" },
        styleOverrides: {
          paper: {
            borderRadius: "var(--wg-radius-xl)",
            border: "1px solid var(--wg-border)",
            boxShadow: "var(--wg-shadow-lg)",
            backgroundColor: "var(--wg-surface)",
            backgroundImage: "none",
            margin: 16,
            width: "calc(100% - 32px)",
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "20px 24px 12px",
            fontSize: "1.0625rem",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: "var(--wg-ink)",
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: { padding: "8px 24px 20px" },
          dividers: {
            borderColor: "var(--wg-border)",
            padding: "20px 24px",
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "14px 24px 20px",
            gap: 8,
            borderTop: "1px solid var(--wg-border)",
          },
        },
      },

      /* --- Menus, popovers, tooltips ------------------------------------ */
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: "var(--wg-radius-md)",
            border: "1px solid var(--wg-border)",
            boxShadow: "var(--wg-shadow-md)",
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "var(--wg-radius-md)",
            border: "1px solid var(--wg-border)",
            boxShadow: "var(--wg-shadow-md)",
            marginTop: 6,
          },
          list: { padding: 6 },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-sm)",
            fontSize: "0.8125rem",
            minHeight: 38,
            "&:hover": { backgroundColor: "var(--wg-surface-hover)" },
            "&.Mui-selected": {
              backgroundColor: "var(--wg-primary-soft)",
              "&:hover": { backgroundColor: "var(--wg-primary-soft-hover)" },
            },
          },
        },
      },

      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 300 },
        styleOverrides: {
          tooltip: {
            backgroundColor: "var(--wg-viz-tooltip-bg)",
            color: "var(--wg-viz-tooltip-ink)",
            fontSize: "0.75rem",
            fontWeight: 500,
            borderRadius: "var(--wg-radius-sm)",
            padding: "6px 10px",
            boxShadow: "var(--wg-shadow-md)",
          },
          arrow: { color: "var(--wg-viz-tooltip-bg)" },
        },
      },

      /* --- Inputs ------------------------------------------------------- */
      MuiTextField: { defaultProps: { size: "small" } },
      MuiFormControl: { defaultProps: { size: "small" } },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-md)",
            backgroundColor: "var(--wg-surface)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--wg-border-strong)",
              transition: "border-color var(--wg-duration) var(--wg-ease)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--wg-primary-border)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: "var(--wg-primary)",
            },
            "&.Mui-focused": { boxShadow: "var(--wg-shadow-focus)" },
          },
          input: { fontSize: "0.875rem" },
        },
      },

      MuiInputLabel: { styleOverrides: { root: { fontSize: "0.875rem" } } },

      MuiSelect: {
        styleOverrides: { select: { fontSize: "0.875rem" } },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: { borderRadius: "var(--wg-radius-xs)", padding: 7 },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8 },
          // Only restyle the OFF track. MUI paints the ON track through a more
          // specific selector, so leaving that alone keeps "on" clearly blue.
          track: {
            borderRadius: 12,
            opacity: 1,
            backgroundColor: "var(--wg-border-strong)",
          },
          switchBase: {
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "var(--wg-primary)",
              opacity: 1,
            },
          },
        },
      },

      /* --- Data display ------------------------------------------------- */
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-pill)",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: 26,
          },
          outlined: { borderColor: "var(--wg-border)" },
          // Only neutral chips get the grey fill. Applying it unconditionally
          // also repainted colour="primary|success|…" chips, which kept their
          // white label — that is what made selected chips unreadable.
          filled: ({ ownerState }) => ({
            ...(ownerState.color === "default" && {
              backgroundColor: "var(--wg-surface-hover)",
            }),
          }),
          label: { paddingInline: 10 },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-md)",
            "&:hover": { backgroundColor: "var(--wg-surface-hover)" },
            "&.Mui-selected": {
              backgroundColor: "var(--wg-primary-soft)",
              "&:hover": { backgroundColor: "var(--wg-primary-soft-hover)" },
            },
          },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: "var(--wg-border)" } } },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: "var(--wg-radius-md)",
            border: "1px solid var(--wg-border)",
            fontSize: "0.8125rem",
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: "var(--wg-radius-pill)", height: 6 },
        },
      },

      MuiCircularProgress: { defaultProps: { thickness: 4 } },
    },
  });

export default buildTheme;
